"use client"

import { useState, useEffect } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Button } from "../ui/button"
import { Alert, AlertDescription } from "../ui/alert"
import { Loader2, Eye, EyeOff, AlertTriangle } from "lucide-react"
import contractLineService, { LineCredentialsDto, UpdateCredentialsDto } from "../../services/contracts/ContractLineService"
import { clientService } from "../../services/clients/ClientService"
import { useToast } from "../../hooks/useToast"
import { useMeuTimVerifyOnDemand } from "../../hooks/useMeuTimVerifyOnDemand"

interface CredentialsModalProps {
  lineId: string | null
  /** Quando não há linha de contrato, edita credenciais no cadastro do cliente */
  clientIdForCredentials?: string | null
  open: boolean
  onClose: () => void
  onCredentialsUpdated: () => void
}

export function CredentialsModal({
  lineId,
  clientIdForCredentials = null,
  open,
  onClose,
  onCredentialsUpdated,
}: CredentialsModalProps) {
  const { showSuccess, showError, showWarning, showInfo } = useToast()
  const { verifying, verifyNow } = useMeuTimVerifyOnDemand()
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [hasExistingCredentials, setHasExistingCredentials] = useState(false)
  const [initialUsername, setInitialUsername] = useState("")
  const [initialPassword, setInitialPassword] = useState("")

  const useClientEndpoint = Boolean(clientIdForCredentials && !lineId)

  useEffect(() => {
    if (open && (lineId || clientIdForCredentials)) {
      loadCredentials()
    } else {
      setUsername("")
      setPassword("")
      setInitialUsername("")
      setInitialPassword("")
      setHasExistingCredentials(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lineId, clientIdForCredentials])

  const loadCredentials = async () => {
    if (!lineId && !clientIdForCredentials) return
    setLoading(true)
    try {
      if (useClientEndpoint && clientIdForCredentials) {
        const credentials = await clientService.getClientMeuTimCredentials(clientIdForCredentials)
        const login = credentials.meuTimLogin || ""
        const senha = credentials.meuTimSenha || ""
        setUsername(login)
        setPassword(senha)
        setInitialUsername(login)
        setInitialPassword(senha)
        setHasExistingCredentials(Boolean(login.trim() && senha.trim()))
      } else if (lineId) {
        const credentials: LineCredentialsDto = await contractLineService.getLineCredentials(lineId)
        if (credentials) {
          const login = credentials.meuTimLogin || ""
          const senha = credentials.meuTimSenha || ""
          setUsername(login)
          setPassword(senha)
          setInitialUsername(login)
          setInitialPassword(senha)
          setHasExistingCredentials(Boolean(login.trim() && senha.trim()))
        }
      }
    } catch (error: any) {
      console.error("Error loading credentials:", error)
      showError("Erro ao carregar credenciais")
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if ((!lineId && !clientIdForCredentials) || !username || !password) return

    if (hasExistingCredentials) {
      const confirmOverwrite = window.confirm(
        "Ja existem credenciais Meu TIM cadastradas. Deseja sobrescrever as credenciais atuais?"
      )

      if (!confirmOverwrite) {
        return
      }
    }

    if (username === initialUsername && password === initialPassword) {
      showError("Nenhuma alteracao detectada nas credenciais.")
      return
    }

    setSaving(true)
    try {
      if (useClientEndpoint && clientIdForCredentials) {
        await clientService.updateClientMeuTimCredentials(clientIdForCredentials, {
          meuTimLogin: username,
          meuTimSenha: password,
        })
      } else if (lineId) {
        const updateData: UpdateCredentialsDto = {
          meuTimLogin: username,
          meuTimSenha: password,
        }
        await contractLineService.updateCredentials(lineId, updateData)
      }
      showSuccess("Credenciais atualizadas com sucesso")
      onCredentialsUpdated()
      onClose()
    } catch (error: any) {
      console.error("Error saving credentials:", error)
      showError(error.response?.data?.message || error.message || "Erro ao salvar credenciais")
    } finally {
      setSaving(false)
    }
  }

  const handleVerifyNow = async () => {
    if (!clientIdForCredentials) return
    const result = await verifyNow(clientIdForCredentials)
    if (result.status === 'ok') {
      showSuccess(result.message)
    } else if (result.status === 'credencial_invalida') {
      showError(result.message)
    } else if (result.status === 'bloqueio_ou_captcha') {
      showWarning(result.message)
    } else {
      showInfo(result.message)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Credenciais Meu TIM</DialogTitle>
          <DialogDescription>
            {useClientEndpoint
              ? "Insira ou visualize as credenciais de acesso ao portal Meu TIM do cliente (cadastro central)."
              : "Insira ou visualize as credenciais de acesso ao portal Meu TIM (gravadas no cadastro do cliente)."}
          </DialogDescription>
          <DialogDescription>
            Estas credenciais sao do app Meu TIM do cliente, nao do usuario do GettelWeb.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4 py-4">
            {hasExistingCredentials && (
              <Alert className="border-amber-300 bg-amber-50 text-amber-900">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Credenciais ja cadastradas. Revise com atencao antes de salvar para evitar sobrescrita acidental.
                </AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="username">Usuário</Label>
              <Input
                id="username"
                name="meu-tim-login"
                autoComplete="off"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="usuario@email.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="meu-tim-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          {clientIdForCredentials && hasExistingCredentials && (
            <Button
              variant="outline"
              onClick={handleVerifyNow}
              disabled={verifying || saving || loading}
              title="Faz um login real no portal Meu TIM agora (até 45s) com as credenciais salvas — não bloqueia esta tela."
            >
              {verifying && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Verificar acesso agora
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving || !username || !password}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
